import React from 'react'
import { useTranslation } from 'react-i18next'

import DOBModal from '@src/quince-components/src/atoms/dobModal'
import { ProductFormSubscriptionPurchaseSection } from '@src/quince-components/src/atoms/productForm/components/ProductFormSubscriptionPurchaseSection'
import Product from '@src/quince-site-common/src/domains/Product'
import productHelper, { isProductAlcohol } from '@src/quince-site-common/src/helpers/productHelper'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import { TPDPSectionData } from '@src/quince-site-pdp/src/types/pdp-section-data.type'

import MiniPDPProductCTA from '../miniPDPProductCTA/miniPDPProductCTA'
import { MiniPDPProductInfo } from '../miniPDPProductInfo/miniPDPProductInfo'
import MiniPDPProductOptions from '../miniPDPProductOptions/miniPDPProductOptions'

import { useMiniPDPProductPurchase } from './hooks/useMiniPDPProductPurchase'
import styles from './miniPDPProductPurchase.module.scss'

export const MiniPDPProductPurchase = ({
  product,
  sectionData,
}: {
  product: Product
  sectionData: TPDPSectionData
}) => {
  const { t } = useTranslation('common')

  const {
    selectedOptions,
    productOptions,
    onOptionSelect,
    aboveFoldTrackingRefs,
    selectedSiblingProductId,
    productPurchaseInfo,
    sizeOptionRef,
    selectedVariant,
    selectedVariantPurchaseInfo,
    onAddToCart,
    onJoinTheWaitList,
    productCTAType,
    areAllVariantOOS,
    productFormErrorMessage,
    inputOptionError,
    selectedPlanId,
    purchaseOption,
    showPurchaseTypeSelector,
    isAddToCartInProgress,
    showErrorMessage,
    showProductSubscriptionDeliveryMessage,
    variantSubscriptionPlans,
    formattedSubscriptionPrice,
    formattedSalePrice,
    formattedTraditionalPrice,
    formattedFestivalOfferPrice,
    savingPercentage,
    festivalPricePerUnit,
    subscriptionOfferPricePerUnit,
    subscriptionFestivalSavingPercentage,
    setPurchaseOption,
    setSelectedPlanId,
    selectedVariantAvailableInventory,
    showVariantAvailableInventory,
    isExpandedSize,
    expandSizeMiniPDP,
    isDOBModalOpen,
    dateOfBirth,
    setDateOfBirth,
    dobErrorMessage,
    setDobErrorMessage,
    disableAgeVerification,
    handleDOBModalSubmit,
    handleDOBModalClose,
    isReturnable,
    brandName,
    brandLink,
    disableAddToCart,
    isCurrentProductOptionLeavingSoon,
    onClickedShopSimilar,
  } = useMiniPDPProductPurchase({ product, sectionData })

  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  const isAgeRestrictedProduct = productHelper.getIsAgeRestrictedProduct(product)
  const pdpCustomMoreInfoText = productHelper.getPDPCustomMoreInfoText(product)

  return (
    <div className={styles.wrapper}>
      <MiniPDPProductInfo
        product={product}
        selectedOptions={selectedOptions}
        selectedVariant={selectedVariant}
        brandName={brandName}
        brandLink={brandLink}
        formattedSubscriptionPrice={formattedSubscriptionPrice}
        formattedSalePrice={formattedSalePrice}
        formattedTraditionalPrice={formattedTraditionalPrice}
        subscriptionOfferPricePerUnit={subscriptionOfferPricePerUnit}
        savingPercentage={savingPercentage}
        formattedFestivalOfferPrice={formattedFestivalOfferPrice}
        festivalPricePerUnit={festivalPricePerUnit}
        purchaseOption={purchaseOption}
      />
      <MiniPDPProductOptions
        product={product}
        productOptions={productOptions}
        selectedOptions={selectedOptions}
        aboveFoldTrackingRefs={aboveFoldTrackingRefs}
        onOptionSelect={onOptionSelect}
        selectedSiblingProductId={selectedSiblingProductId}
        productPurchaseInfo={productPurchaseInfo}
        inputOptionError={inputOptionError}
        sizeOptionRef={sizeOptionRef}
        isExpandedSize={isExpandedSize}
        expandSizeMiniPDP={expandSizeMiniPDP}
      />

      {/* grey box message for age restricted products and non-returnable products */}
      {pdpCustomMoreInfoText && isAgeRestrictedProduct && (
        <div className={styles.informationBox}>
          <div
            //eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: getSanitizedHtml(pdpCustomMoreInfoText) }}
          />
          {isProductAlcohol(product) && (
            <div className={styles.additionalMessage}>{`${t('not_returnable_food_safety')}.`}</div>
          )}
        </div>
      )}
      {!isReturnable && !isAgeRestrictedProduct && (
        <div className={styles.informationBox}>{t('this_product_is_not_returnable')}</div>
      )}

      <ProductFormSubscriptionPurchaseSection
        showProductSubscriptionDeliveryMessage={showProductSubscriptionDeliveryMessage}
        subscribedPlan={product?.subscription?.subscribedPlan}
        showNewPdpDesignV2={true}
        selectedVariantPurchaseInfoShowWaitList={selectedVariantPurchaseInfo?.showWaitList}
        productCTAType={productCTAType}
        isRecurringItemSubscriptionsExpEnabled={isRecurringItemSubscriptionsExpEnabled}
        subscriptionEnabled={product?.subscriptionEnabled}
        productPurchaseInfo={productPurchaseInfo}
        showPurchaseTypeSelector={showPurchaseTypeSelector}
        product={product}
        purchaseOption={purchaseOption}
        setPurchaseOption={setPurchaseOption}
        selectedPlanId={selectedPlanId}
        setSelectedPlanId={setSelectedPlanId}
        isFromCartPopup={false}
        isBuyNow={false}
        variantSubscriptionPlans={variantSubscriptionPlans}
        formattedSubscriptionPrice={formattedSubscriptionPrice}
        formattedSalePrice={formattedSalePrice}
        formattedFestivalOfferPrice={formattedFestivalOfferPrice}
        subscriptionFestivalSavingPercentage={subscriptionFestivalSavingPercentage}
        subscriptionId={product?.subscription?.subscribedPlan?.subscriptionId}
        showVariantAvailableInventory={showVariantAvailableInventory}
        selectedVariantAvailableInventory={selectedVariantAvailableInventory}
      />
      <MiniPDPProductCTA
        product={product}
        selectedOptions={selectedOptions}
        selectedVariantPurchaseInfo={selectedVariantPurchaseInfo}
        productCTAType={productCTAType}
        areAllVariantOOS={areAllVariantOOS}
        inputOptionError={inputOptionError}
        productFormErrorMessage={showErrorMessage ? productFormErrorMessage : ''}
        onAddToCart={onAddToCart}
        onJoinTheWaitList={onJoinTheWaitList}
        selectedPlanId={selectedPlanId}
        purchaseOption={purchaseOption}
        showPurchaseTypeSelector={showPurchaseTypeSelector}
        isAddToCartInProgress={isAddToCartInProgress}
        selectedVariantAvailableInventory={selectedVariantAvailableInventory}
        showVariantAvailableInventory={showVariantAvailableInventory}
        disableAddToCart={disableAddToCart}
        isCurrentProductOptionLeavingSoon={isCurrentProductOptionLeavingSoon}
        onClickedShopSimilar={onClickedShopSimilar}
      />

      {isDOBModalOpen && !disableAgeVerification && (
        <DOBModal
          onClose={handleDOBModalClose}
          dateOfBirth={dateOfBirth}
          setDateOfBirth={setDateOfBirth}
          open={isDOBModalOpen}
          onSubmit={handleDOBModalSubmit}
          loader={isAddToCartInProgress ?? false}
          dobErrorMessage={dobErrorMessage}
          setDobErrorMessage={setDobErrorMessage}
          ageLimit={productHelper.getAgeLimitInYears(product) ?? 0}
        />
      )}
    </div>
  )
}
